/**
 * The Project Brain page — status and wiring, Canon and corrections,
 * operations authority, and the constitutional-floor export/delete controls.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05A, over C02C's
 * `auteur_cloud/brain/**`.
 *
 * MEMORY AND AUTHORITY ARE VISUALLY DISTINCT (required property)
 *   This page never mixes the two planes in one list or one style:
 *   memory-side sections carry `pb-memory-*` classes and a plain reading
 *   style; authority-side sections (Canon approvals, operations authority,
 *   lifecycle decisions) carry `pb-authority-*` classes and an explicit
 *   "Authority" heading, so a reader can always tell a remembered fact from
 *   a decision about who may change one.
 *
 * CANON AND CORRECTION FLOWS — THE UI ASKS, THE SERVER DECIDES
 *   A correction submitted here enters the governed plane as a pending,
 *   human-authored claim; it never becomes Canon client-side. Canon approval
 *   sends the reviewer's `observed_sha256` to `CanonIndex.approve()`, which
 *   is human-only by three independent server layers (database CHECK, the
 *   single gated writer, and the capability model). If the reviewed digest
 *   drifted from the indexed digest the server refuses with
 *   `canon_index_stale`; the UI surfaces that refusal verbatim rather than
 *   retrying or guessing.
 *
 * EXPORT/DELETE ARE UNCONDITIONAL (constitutional floor)
 *   `lifecycle.py` places export and deletion in `NEVER_GATE`
 *   (capability `CAP-DELETION`). The buttons here take no plan argument and
 *   the page never frames them as features of a tier. Deletion asks for a
 *   typed confirmation in the UI, but that is a friction step — the
 *   authority for deletion never comes from the plan.
 *
 * VISIBLE ORCHESTRATION (Builder Round P2, binding): every section states
 * Loading / Error / Unavailable before it claims content. `brain_status()`
 * is fetched first and surfaced in a banner: `deployed: false` or
 * `mounted: false` renders as an honest "not wired yet" banner, not as an
 * empty page pretending to work. No fabricated brain state.
 *
 * BUILD STATUS: compiled and exercised as part of the Vite customer SPA.
 */

import React, { useCallback, useEffect, useState } from 'react';

import { PendingReviewPanel } from './PendingReviewPanel';
import {
  AuthorityDecisionView,
  BrainApiError,
  BrainStatus,
  CanonEntryView,
  LifecycleRequestView,
  fetchBrainStatus,
  fetchCanonIndex,
  fetchOperationsAuthority,
  requestBrainDeletion,
  requestBrainExport,
  requestCanonApproval,
  submitCorrection,
} from '../../api/brain';

export interface ProjectBrainPageProps {
  projectId: string;
}

type LoadState = 'loading' | 'error' | 'unavailable' | 'ready';

function SectionState({ state, onRetry }: { state: LoadState; onRetry: () => void }): React.ReactElement | null {
  if (state === 'loading') return <p className="pb-section-state">Loading…</p>;
  if (state === 'unavailable') {
    return <p className="pb-section-state">Unavailable — route not mounted at this deployment.</p>;
  }
  if (state === 'error') {
    return (
      <p className="pb-section-state">
        Could not load. <button type="button" onClick={onRetry}>Retry</button>
      </p>
    );
  }
  return null;
}

export function ProjectBrainPage({ projectId }: ProjectBrainPageProps): React.ReactElement {
  const [status, setStatus] = useState<BrainStatus | null>(null);
  const [statusState, setStatusState] = useState<LoadState>('loading');

  const [canon, setCanon] = useState<CanonEntryView[]>([]);
  const [canonState, setCanonState] = useState<LoadState>('loading');

  const [authority, setAuthority] = useState<AuthorityDecisionView[]>([]);
  const [authorityState, setAuthorityState] = useState<LoadState>('loading');

  const [correctionNote, setCorrectionNote] = useState('');
  const [correctionRecordId, setCorrectionRecordId] = useState('');
  const [correctionMessage, setCorrectionMessage] = useState<string | null>(null);

  const [lifecycleMessage, setLifecycleMessage] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletionRequested, setDeletionRequested] = useState<LifecycleRequestView | null>(null);

  const loadStatus = useCallback(() => {
    setStatusState('loading');
    fetchBrainStatus()
      .then((result) => { setStatus(result); setStatusState('ready'); })
      .catch((error: unknown) => {
        if (error instanceof BrainApiError && error.status === 404) setStatusState('unavailable');
        else setStatusState('error');
      });
  }, []);

  const loadCanon = useCallback(() => {
    setCanonState('loading');
    fetchCanonIndex(projectId)
      .then((result) => { setCanon(result); setCanonState('ready'); })
      .catch((error: unknown) => {
        if (error instanceof BrainApiError && error.status === 404) setCanonState('unavailable');
        else setCanonState('error');
      });
  }, [projectId]);

  const loadAuthority = useCallback(() => {
    setAuthorityState('loading');
    fetchOperationsAuthority(projectId)
      .then((result) => { setAuthority(result); setAuthorityState('ready'); })
      .catch((error: unknown) => {
        if (error instanceof BrainApiError && error.status === 404) setAuthorityState('unavailable');
        else setAuthorityState('error');
      });
  }, [projectId]);

  useEffect(() => { loadStatus(); loadCanon(); loadAuthority(); }, [loadStatus, loadCanon, loadAuthority]);

  const onSubmitCorrection = useCallback(() => {
    setCorrectionMessage(null);
    if (!correctionRecordId || !correctionNote.trim()) {
      setCorrectionMessage('A record identifier and a note are both required.');
      return;
    }
    submitCorrection(projectId, correctionRecordId, correctionNote.trim())
      .then((event) => {
        setCorrectionMessage(
          `Correction recorded as ${event.reason_code}. It is a pending human claim, not Canon.`,
        );
        setCorrectionNote('');
      })
      .catch(() => setCorrectionMessage('Correction could not be submitted.'));
  }, [projectId, correctionRecordId, correctionNote]);

  const onApproveCanon = useCallback((entry: CanonEntryView) => {
    // The reviewer's digest goes to the server as-is; the server is the only
    // authority that can mark an entry approved, and only for humans.
    requestCanonApproval(entry.entry_id, entry.observed_sha256 ?? '')
      .then(() => loadCanon())
      .catch((error: unknown) => {
        const detail = error instanceof BrainApiError ? ` (${error.status})` : '';
        setCorrectionMessage(`Canon approval refused by the server${detail}.`);
      });
  }, [loadCanon]);

  const onExport = useCallback(() => {
    setLifecycleMessage(null);
    requestBrainExport(projectId)
      .then((request) => setLifecycleMessage(`Export ${request.state} (${request.reason_code}).`))
      .catch(() => setLifecycleMessage('Export request failed.'));
  }, [projectId]);

  const onDeletion = useCallback(() => {
    if (deleteConfirm !== 'DELETE') {
      setLifecycleMessage('Type DELETE to confirm deletion. This is irreversible.');
      return;
    }
    requestBrainDeletion(projectId)
      .then((request) => {
        setDeletionRequested(request);
        setLifecycleMessage(`Deletion ${request.state} (${request.reason_code}).`);
      })
      .catch(() => setLifecycleMessage('Deletion request failed.'));
  }, [projectId, deleteConfirm]);

  return (
    <div className="pb-page" aria-label="Project Brain">
      <header className="pb-header">
        <h2>Project Brain</h2>
        <p className="pb-subtitle">Memory, Canon, Focus Pack authority, and data controls for this project.</p>
      </header>

      {/* Visible Orchestration: the wiring state is shown, not assumed. */}
      <section className="pb-wiring" aria-label="Brain wiring status">
        <SectionState state={statusState} onRetry={loadStatus} />
        {statusState === 'ready' && status !== null && (
          <p className="pb-wiring-line">
            Brain tables: {status.tables} · migration applied: {String(status.migration_applied)} ·
            deployed: {String(status.deployed)} · mounted: {String(status.mounted)}
          </p>
        )}
        {statusState === 'ready' && status !== null && (!status.deployed || !status.mounted) && (
          <p className="pb-wiring-warning">
            The hosted brain is not fully wired at this deployment. Sections below report
            their true state; nothing here is fabricated.
          </p>
        )}
      </section>

      {/* MEMORY PLANE — plain reading style, `pb-memory-*`. */}
      <section className="pb-memory-plane" aria-label="Memory">
        <h3>Memory</h3>
        <p className="pb-plane-note">
          Remembered facts and their provenance. See the Context &amp; Memory page for the
          full record list, source references, and freshness.
        </p>
        <p className="pb-link">Open Context &amp; Memory for records, trail, and corrections.</p>
      </section>

      {/* AUTHORITY PLANE — distinct heading and `pb-authority-*` styling. */}
      <section className="pb-authority-plane" aria-label="Authority">
        <h3>Authority</h3>
        <p className="pb-plane-note">
          Decisions about who may approve Canon and operate on the brain. This is not
          memory; it is governance over memory.
        </p>

        <PendingReviewPanel projectId={projectId} />

        <div className="pb-authority-block">
          <h4>Canon index</h4>
          <SectionState state={canonState} onRetry={loadCanon} />
          {canonState === 'ready' && canon.length === 0 && <p className="pb-empty">No Canon entries indexed.</p>}
          {canonState === 'ready' && canon.length > 0 && (
            <ul className="pb-authority-list">
              {canon.map((entry) => (
                <li key={entry.entry_id} className="pb-authority-row" data-state={entry.approval_state}>
                  <code className="pb-canon-path">{entry.path_ref}</code>
                  <span className="pb-canon-state">{entry.approval_state}</span>
                  <span className="pb-canon-freshness">freshness: {entry.freshness}</span>
                  <span className="pb-canon-approver">
                    {entry.approved_by_principal_kind
                      ? `approved by ${entry.approved_by_principal_kind} at ${entry.approved_at ?? ''}`
                      : 'unapproved'}
                  </span>
                  {entry.approval_state === 'pending' && (
                    <button type="button" onClick={() => onApproveCanon(entry)}>
                      Request approval (human only)
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="pb-authority-block">
          <h4>Operations authority</h4>
          <SectionState state={authorityState} onRetry={loadAuthority} />
          {authorityState === 'ready' && authority.length === 0 && <p className="pb-empty">No operations recorded.</p>}
          {authorityState === 'ready' && authority.length > 0 && (
            <ul className="pb-authority-list">
              {authority.map((decision, index) => (
                <li key={`${decision.operation}-${index}`} className="pb-authority-row">
                  <span className="pb-op-name">{decision.operation}</span>
                  <span className="pb-op-allowed">{decision.allowed ? 'allowed' : 'denied'}</span>
                  <span className="pb-op-reason">{decision.reason_code}</span>
                  <span className="pb-op-decider">decided by {decision.decided_by_kind}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* CORRECTION FLOW — human-authored claim, enters as pending only. */}
      <section className="pb-corrections" aria-label="Corrections">
        <h3>Corrections</h3>
        <p className="pb-plane-note">
          A correction is a human claim about a memory record. It is recorded with a
          reason code and stays pending — it never becomes Canon or trusted memory
          without the approval gate.
        </p>
        <label className="pb-field">
          Record identifier
          <input
            value={correctionRecordId}
            onChange={(event) => setCorrectionRecordId(event.target.value)}
            placeholder="mem_…"
          />
        </label>
        <label className="pb-field">
          Note
          <textarea
            value={correctionNote}
            onChange={(event) => setCorrectionNote(event.target.value)}
            placeholder="What is wrong, and what is correct instead."
          />
        </label>
        <button type="button" onClick={onSubmitCorrection}>Submit correction</button>
        {correctionMessage !== null && <p className="pb-inline-message">{correctionMessage}</p>}
      </section>

      {/* EXPORT / DELETE — constitutional floor, NEVER_GATE, no plan argument. */}
      <section className="pb-data-controls" aria-label="Export and deletion">
        <h3>Export and deletion</h3>
        <p className="pb-plane-note">
          Export and deletion are unconditional rights in the constitutional floor
          (never plan-gated). Deletion is irreversible; the server performs it only
          after its own lifecycle preflight.
        </p>
        <button type="button" onClick={onExport}>Request export</button>
        <div className="pb-delete-row">
          <input
            value={deleteConfirm}
            onChange={(event) => setDeleteConfirm(event.target.value)}
            placeholder='Type "DELETE" to confirm'
          />
          <button type="button" onClick={onDeletion}>Request deletion</button>
        </div>
        {deletionRequested !== null && (
          <p className="pb-inline-message pb-delete-notice">
            Deletion request {deletionRequested.request_id} is {deletionRequested.state}.
          </p>
        )}
        {lifecycleMessage !== null && <p className="pb-inline-message">{lifecycleMessage}</p>}
      </section>
    </div>
  );
}

export default ProjectBrainPage;
